import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { apiBasePath, isPublicServicePath } from 'src/modules/api-service';
import { UCP_PROXY_PATH_PREFIX } from 'src/modules/global-vars';

import { SessionManager } from './session';

const apiOrigin = new URL(apiBasePath, window.location.origin).origin;

const describeRequest = (config?: AxiosRequestConfig) => `${config?.method?.toUpperCase()} ${config?.url}`;

/**
 * A 401 only means "the session is dead" for requests carrying our JWT to our own API.
 *
 * Keyed off the request rather than `window.location`, which can already have changed while the
 * request was in flight. The UCP proxy routes are excluded because they authenticate the backend
 * against an upstream, so a 401 there says nothing about the user's session -- treating one as a
 * session death logged people out spuriously.
 */
const isSessionAuthRequest = (config?: AxiosRequestConfig) => {
  if (!config?.url) return false;

  const { origin, pathname } = new URL(config.url, config.baseURL || window.location.origin);

  return origin === apiOrigin && !pathname.startsWith(UCP_PROXY_PATH_PREFIX);
};

/**
 * Installs the session interceptor on an axios instance and returns its handle, so callers can
 * eject it. Explicit rather than a side effect of importing this module, which makes the install
 * order in `index.tsx` visible and lets tests install onto their own instance.
 */
export const installSessionInterceptor = (axiosInstance: AxiosInstance, sessionManager: SessionManager) =>
  axiosInstance.interceptors.response.use(
    (response) => {
      // An authenticated response proves the session works, which re-arms seamless SSO for the next
      // expiry. Strictly more reliable than keying off sign-in: bounces that never reach the
      // `?access_token=` hand-off would otherwise leave the attempt marker set for the whole
      // browser session and silently disarm the next genuine re-authentication.
      if (isSessionAuthRequest(response.config)) sessionManager.onAuthenticatedResponse();

      return response;
    },
    (error: AxiosError) => {
      if (error.response?.status === 401 && isSessionAuthRequest(error.config) && !isPublicServicePath()) {
        if (sessionManager.onUnauthorized()) {
          // The only trace the redirect leaves -- the error is suppressed and the document replaced.
          console.warn(`[session] 401 on ${describeRequest(error.config)} -- redirecting to login`);

          // The navigation is in flight but does not unload synchronously. Rejecting would let
          // Suspense render the app error boundary first, flashing it right before the browser
          // navigates away. Never settling means nothing downstream ever sees this error.
          return new Promise<never>(() => {});
        }

        // Nothing was started, so suppressing here would hang every consumer for the life of the
        // document -- that is the permanent-spinner soft-lock on /login. The session module has
        // already dropped the rejected token so a reload converges; let the error through so the
        // error boundary's reset button stays reachable.
        console.error(`[session] 401 on ${describeRequest(error.config)} without a redirect -- token dropped`);
      }

      return Promise.reject(error);
    },
  );
